import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Building2, Cable, CalendarDays, Columns3, LayoutDashboard, LogOut, Menu, MessagesSquare, Shuffle, Stethoscope, Users, UsersRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { NotificationBell } from "@/components/crm/NotificationBell";

const MAIN = [
  { to: "/crm/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/crm/leads", label: "Leads", icon: UsersRound, id: "leads" },
  { to: "/crm/pipeline", label: "Pipeline", icon: Columns3, id: "pipeline" },
  { to: "/crm/conversaciones", label: "Conversaciones", icon: MessagesSquare, id: "conversaciones" },
  { to: "/crm/citas", label: "Citas", icon: CalendarDays, id: "citas" },
];
const ADMIN = [
  { to: "/crm/admin/usuarios", label: "Usuarios", icon: Users, id: "usuarios" },
  { to: "/crm/admin/servicios", label: "Servicios", icon: Stethoscope, id: "servicios" },
  { to: "/crm/admin/centro", label: "Centro y contacto", icon: Building2, id: "centro" },
  { to: "/crm/admin/asignacion", label: "Reglas de asignación", icon: Shuffle, id: "asignacion" },
  { to: "/crm/admin/integraciones", label: "Integraciones", icon: Cable, id: "integraciones" },
];

const NavItem = ({ item, onClick }) => (
  <NavLink to={item.to} onClick={onClick} data-testid={`sidebar-link-${item.id}`}
           className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
             isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
    <item.icon className="h-[18px] w-[18px]" /> {item.label}
  </NavLink>
);

const SidebarBody = ({ user, onNavigate, onLogout }) => (
  <div className="flex h-full flex-col bg-[#0B2545] p-4 text-white">
    <div className="py-1"><Logo light variant="sidebar" /></div>
    <nav className="mt-8 flex-1 space-y-1" data-testid="sidebar-nav">
      {MAIN.map((i) => <NavItem key={i.id} item={i} onClick={onNavigate} />)}
      {user.role === "admin" && (
        <div className="pt-6" data-testid="sidebar-admin-section">
          <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35">Configuración</p>
          {ADMIN.map((i) => <NavItem key={i.id} item={i} onClick={onNavigate} />)}
        </div>
      )}
    </nav>
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      <p className="truncate text-sm font-semibold" data-testid="current-user-name">{user.name}</p>
      <p className="truncate text-xs text-white/50" data-testid="current-user-role">{ROLE_LABELS[user.role]}</p>
      <button onClick={onLogout} className="mt-3 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              data-testid="logout-button"><LogOut className="h-3.5 w-3.5" /> Cerrar sesión</button>
    </div>
  </div>
);

export const CrmLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#F5F7FA] text-[#0B2545]" data-testid="crm-layout">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:block"><SidebarBody user={user} onLogout={doLogout} /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 p-0">
          <SheetTitle className="sr-only">Menú</SheetTitle>
          <SidebarBody user={user} onNavigate={() => setOpen(false)} onLogout={doLogout} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl lg:hidden">
          <button onClick={() => setOpen(true)} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200" data-testid="crm-mobile-menu-button">
            <Menu className="h-4 w-4" />
          </button>
          <Logo compact />
          <div className="ml-auto"><NotificationBell testid="notification-bell-mobile" /></div>
        </header>
        <div className="hidden justify-end px-10 pt-6 lg:flex"><NotificationBell /></div>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:px-10 lg:pb-10 lg:pt-2"><Outlet /></main>
      </div>
    </div>
  );
};
